// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module NeonAnalyticsStore
 * @package @geenius/analytics/neon
 * @description Neon/Postgres analytics provider contract with append-only
 * migrations, a deterministic in-memory test fallback, and a native
 * @neondatabase/serverless client factory for production use.
 */

import type {
  AnalyticsDashboardData,
  AnalyticsDashboardQuery,
  AnalyticsEvent,
  AnalyticsEventQuery,
  AnalyticsPageViewQuery,
  AnalyticsReadResult,
  CohortAnalysis,
  CohortAnalysisQuery,
  FunnelResult,
  FunnelResultsQuery,
  FunnelStep,
  PageView,
  RealtimeStats,
  RealtimeStatsQuery,
  TimeRange,
  UserTraits,
} from "@geenius/analytics";

/**
 * SQL execution callback used by the Neon analytics store.
 *
 * @param sql SQL statement to execute.
 * @param parameters Optional positional parameters for the statement.
 * @returns Driver-specific result payload.
 */
export type NeonSqlExecutor = (
  sql: string,
  parameters?: readonly unknown[],
) => Promise<unknown>;

/**
 * Minimal SQL client contract accepted by the Neon store factory.
 */
export interface NeonAnalyticsClient {
  query(sql: string, parameters?: readonly unknown[]): Promise<unknown>;
}

/**
 * Options used to create the Neon analytics store.
 */
export interface NeonAnalyticsStoreOptions {
  execute?: NeonSqlExecutor;
  /**
   * Tenant identifier written to analytics rows and used to scope SQL reads.
   * Use this in multi-tenant Neon deployments alongside the packaged RLS policy.
   */
  tenantId?: string;
}

/**
 * Public Neon analytics store contract for event, page-view, identity, and migration operations.
 *
 * @property setUserProperties Merges profile traits into a stored Neon user row.
 * @property flush Resolves after synchronous SQL-backed writes have completed.
 * @property shutdown Closes owned connection resources when the store created them.
 * @property purgeExpiredData Deletes records older than the retention cutoff.
 */
export interface NeonAnalyticsStore {
  trackEvent(event: AnalyticsEvent): Promise<AnalyticsEvent>;
  trackPageView(pageView: PageView): Promise<PageView>;
  identifyUser(userId: string, traits?: UserTraits): Promise<void>;
  setUserProperties(userId: string, traits: UserTraits): Promise<void>;
  listEvents(): Promise<AnalyticsEvent[]>;
  getEvents(
    query?: AnalyticsEventQuery,
  ): Promise<AnalyticsReadResult<AnalyticsEvent>>;
  getPageViews(
    query?: AnalyticsPageViewQuery,
  ): Promise<AnalyticsReadResult<PageView>>;
  getDashboardData(
    query?: AnalyticsDashboardQuery,
  ): Promise<AnalyticsDashboardData>;
  getRealtimeStats(query?: RealtimeStatsQuery): Promise<RealtimeStats>;
  getFunnelResults(query: FunnelResultsQuery): Promise<FunnelResult>;
  getCohortAnalysis(query?: CohortAnalysisQuery): Promise<CohortAnalysis>;
  flush(): Promise<void>;
  shutdown(): Promise<void>;
  purgeExpiredData(cutoffTimestamp: number): Promise<void>;
  deleteUserData(userId: string): Promise<void>;
  runMigrations(): Promise<void>;
}

/**
 * Append-only SQL migrations required by the Neon analytics provider.
 */
export const analyticsMigrations = [
  {
    id: "0001_create_analytics_events",
    sql: `CREATE TABLE IF NOT EXISTS geenius_analytics_events (
  id text PRIMARY KEY,
  type text NOT NULL,
  name text NOT NULL,
  properties jsonb,
  url text NOT NULL DEFAULT '',
  referrer text NOT NULL DEFAULT '',
  session_id text NOT NULL,
  user_id text,
  device jsonb NOT NULL,
  timestamp bigint NOT NULL
);`,
  },
  {
    id: "0002_create_analytics_pageviews",
    sql: `CREATE TABLE IF NOT EXISTS geenius_analytics_pageviews (
  id bigserial PRIMARY KEY,
  url text NOT NULL,
  title text NOT NULL DEFAULT '',
  referrer text NOT NULL DEFAULT '',
  duration integer,
  session_id text NOT NULL,
  user_id text,
  timestamp bigint NOT NULL
);`,
  },
  {
    id: "0003_create_analytics_users",
    sql: `CREATE TABLE IF NOT EXISTS geenius_analytics_users (
  user_id text PRIMARY KEY,
  traits jsonb,
  updated_at bigint NOT NULL
);`,
  },
  {
    id: "0004_enable_tenant_rls",
    sql: `ALTER TABLE geenius_analytics_events
  ADD COLUMN IF NOT EXISTS tenant_id text NOT NULL DEFAULT 'default';
ALTER TABLE geenius_analytics_pageviews
  ADD COLUMN IF NOT EXISTS tenant_id text NOT NULL DEFAULT 'default';
ALTER TABLE geenius_analytics_users
  ADD COLUMN IF NOT EXISTS tenant_id text NOT NULL DEFAULT 'default';
CREATE INDEX IF NOT EXISTS geenius_analytics_events_tenant_idx
  ON geenius_analytics_events (tenant_id, timestamp);
CREATE INDEX IF NOT EXISTS geenius_analytics_pageviews_tenant_idx
  ON geenius_analytics_pageviews (tenant_id, timestamp);
CREATE INDEX IF NOT EXISTS geenius_analytics_users_tenant_idx
  ON geenius_analytics_users (tenant_id);
ALTER TABLE geenius_analytics_events ENABLE ROW LEVEL SECURITY;
ALTER TABLE geenius_analytics_pageviews ENABLE ROW LEVEL SECURITY;
ALTER TABLE geenius_analytics_users ENABLE ROW LEVEL SECURITY;
ALTER TABLE geenius_analytics_events FORCE ROW LEVEL SECURITY;
ALTER TABLE geenius_analytics_pageviews FORCE ROW LEVEL SECURITY;
ALTER TABLE geenius_analytics_users FORCE ROW LEVEL SECURITY;
DO $$
BEGIN
  IF NOT EXISTS (
    SELECT 1 FROM pg_policies
    WHERE schemaname = current_schema()
      AND tablename = 'geenius_analytics_events'
      AND policyname = 'geenius_analytics_events_tenant_isolation'
  ) THEN
    CREATE POLICY geenius_analytics_events_tenant_isolation
      ON geenius_analytics_events
      TO PUBLIC
      USING (tenant_id = COALESCE(NULLIF(current_setting('geenius.analytics_tenant_id', true), ''), 'default'))
      WITH CHECK (tenant_id = COALESCE(NULLIF(current_setting('geenius.analytics_tenant_id', true), ''), 'default'));
  END IF;
  IF NOT EXISTS (
    SELECT 1 FROM pg_policies
    WHERE schemaname = current_schema()
      AND tablename = 'geenius_analytics_pageviews'
      AND policyname = 'geenius_analytics_pageviews_tenant_isolation'
  ) THEN
    CREATE POLICY geenius_analytics_pageviews_tenant_isolation
      ON geenius_analytics_pageviews
      TO PUBLIC
      USING (tenant_id = COALESCE(NULLIF(current_setting('geenius.analytics_tenant_id', true), ''), 'default'))
      WITH CHECK (tenant_id = COALESCE(NULLIF(current_setting('geenius.analytics_tenant_id', true), ''), 'default'));
  END IF;
  IF NOT EXISTS (
    SELECT 1 FROM pg_policies
    WHERE schemaname = current_schema()
      AND tablename = 'geenius_analytics_users'
      AND policyname = 'geenius_analytics_users_tenant_isolation'
  ) THEN
    CREATE POLICY geenius_analytics_users_tenant_isolation
      ON geenius_analytics_users
      TO PUBLIC
      USING (tenant_id = COALESCE(NULLIF(current_setting('geenius.analytics_tenant_id', true), ''), 'default'))
      WITH CHECK (tenant_id = COALESCE(NULLIF(current_setting('geenius.analytics_tenant_id', true), ''), 'default'));
  END IF;
END $$;`,
  },
  {
    id: "0005_add_query_filter_indexes",
    sql: `CREATE INDEX IF NOT EXISTS geenius_analytics_events_tenant_user_idx
  ON geenius_analytics_events (tenant_id, user_id, timestamp);
CREATE INDEX IF NOT EXISTS geenius_analytics_events_tenant_session_idx
  ON geenius_analytics_events (tenant_id, session_id, timestamp);
CREATE INDEX IF NOT EXISTS geenius_analytics_events_tenant_name_idx
  ON geenius_analytics_events (tenant_id, name, timestamp);
CREATE INDEX IF NOT EXISTS geenius_analytics_events_tenant_type_idx
  ON geenius_analytics_events (tenant_id, type, timestamp);
CREATE INDEX IF NOT EXISTS geenius_analytics_pageviews_tenant_url_idx
  ON geenius_analytics_pageviews (tenant_id, url, timestamp);
CREATE INDEX IF NOT EXISTS geenius_analytics_pageviews_tenant_user_idx
  ON geenius_analytics_pageviews (tenant_id, user_id, timestamp);
CREATE INDEX IF NOT EXISTS geenius_analytics_pageviews_tenant_session_idx
  ON geenius_analytics_pageviews (tenant_id, session_id, timestamp);`,
  },
] as const;

/**
 * Applies the analytics migration set using the supplied SQL executor.
 *
 * @param execute SQL executor bound to a Neon or Postgres connection.
 * @returns A promise that settles when every migration has been applied idempotently.
 */
export async function runAnalyticsMigrations(
  execute: NeonSqlExecutor,
): Promise<void> {
  await execute(`CREATE TABLE IF NOT EXISTS geenius_analytics_migrations (
  id text PRIMARY KEY,
  applied_at bigint NOT NULL
);`);

  for (const migration of analyticsMigrations) {
    await execute(migration.sql);
    await execute(
      "INSERT INTO geenius_analytics_migrations (id, applied_at) VALUES ($1, $2) ON CONFLICT (id) DO NOTHING",
      [migration.id, Date.now()],
    );
  }
}

function getRows(result: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(result)) return result as Array<Record<string, unknown>>;
  if (result && typeof result === "object" && "rows" in result) {
    const rows = (result as { rows?: unknown }).rows;
    return Array.isArray(rows) ? (rows as Array<Record<string, unknown>>) : [];
  }
  return [];
}

function optionalNumber(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function matchesTimeRange(
  timestamp: number,
  timeRange: TimeRange | undefined,
): boolean {
  if (!timeRange) return true;
  return timestamp >= timeRange.start && timestamp <= timeRange.end;
}

function paginate<T>(
  items: T[],
  limit: number | undefined,
  cursor: string | undefined,
): AnalyticsReadResult<T> {
  const offset = cursor ? Number.parseInt(cursor, 10) : 0;
  const safeOffset = Number.isFinite(offset) && offset > 0 ? offset : 0;
  const safeLimit =
    typeof limit === "number" && limit > 0 ? Math.floor(limit) : items.length;
  const page = items.slice(safeOffset, safeOffset + safeLimit);
  const nextOffset = safeOffset + page.length;
  return {
    items: page,
    nextCursor: nextOffset < items.length ? String(nextOffset) : undefined,
    total: items.length,
  };
}

function cursorOffset(cursor: string | undefined): number {
  if (!cursor) return 0;
  const offset = Number.parseInt(cursor, 10);
  return Number.isFinite(offset) && offset > 0 ? offset : 0;
}

function appendSqlPredicate(
  predicates: string[],
  parameters: unknown[],
  sql: string,
  value: unknown,
): void {
  parameters.push(value);
  predicates.push(sql.replace("?", `$${parameters.length}`));
}

function appendSqlPagination(
  sql: string,
  parameters: unknown[],
  limit: number | undefined,
  cursor: string | undefined,
): string {
  const offset = cursorOffset(cursor);
  let nextSql = sql;
  if (typeof limit === "number" && limit > 0) {
    parameters.push(Math.floor(limit));
    nextSql += ` LIMIT $${parameters.length}`;
  }
  if (offset > 0) {
    parameters.push(offset);
    nextSql += ` OFFSET $${parameters.length}`;
  }
  return nextSql;
}

function sqlReadResult<T>(
  rows: Array<Record<string, unknown>>,
  items: T[],
  limit: number | undefined,
  cursor: string | undefined,
): AnalyticsReadResult<T> {
  const total =
    rows.length > 0 && rows[0]?.__total_count !== undefined
      ? Number(rows[0].__total_count)
      : items.length;
  const nextOffset = cursorOffset(cursor) + items.length;
  return {
    items,
    nextCursor:
      typeof limit === "number" && limit > 0 && nextOffset < total
        ? String(nextOffset)
        : undefined,
    total,
  };
}

function filterEvents(
  events: AnalyticsEvent[],
  query: AnalyticsEventQuery = {},
): AnalyticsEvent[] {
  return events.filter((event) => {
    if (query.userId && event.userId !== query.userId) return false;
    if (query.sessionId && event.sessionId !== query.sessionId) return false;
    if (query.eventName && event.name !== query.eventName) return false;
    if (query.eventType && event.type !== query.eventType) return false;
    return matchesTimeRange(event.timestamp, query.timeRange);
  });
}

function filterPageViews(
  pageViews: PageView[],
  query: AnalyticsPageViewQuery = {},
): PageView[] {
  return pageViews.filter((pageView) => {
    if (query.url && pageView.url !== query.url) return false;
    if (query.userId && pageView.userId !== query.userId) return false;
    if (query.sessionId && pageView.sessionId !== query.sessionId) return false;
    return matchesTimeRange(pageView.timestamp, query.timeRange);
  });
}

function buildDashboardData(
  pageViews: PageView[],
  events: AnalyticsEvent[],
  topPageLimit: number | undefined,
): AnalyticsDashboardData {
  const topPageSummaries = new Map<
    string,
    {
      views: number;
      visitorIds: Set<string>;
      durationTotal: number;
      durationCount: number;
    }
  >();
  const referrerCounts = new Map<string, number>();
  const browserCounts = new Map<string, number>();
  const visitorIds = new Set<string>();
  const deviceBreakdown: AnalyticsDashboardData["deviceBreakdown"] = {
    desktop: 0,
    tablet: 0,
    mobile: 0,
  };

  for (const pageView of pageViews) {
    const visitorId = pageView.userId ?? pageView.sessionId;
    visitorIds.add(visitorId);

    const summary = topPageSummaries.get(pageView.url) ?? {
      views: 0,
      visitorIds: new Set<string>(),
      durationTotal: 0,
      durationCount: 0,
    };
    summary.views += 1;
    summary.visitorIds.add(visitorId);
    if (typeof pageView.duration === "number") {
      summary.durationTotal += pageView.duration;
      summary.durationCount += 1;
    }
    topPageSummaries.set(pageView.url, summary);

    if (pageView.referrer) {
      referrerCounts.set(
        pageView.referrer,
        (referrerCounts.get(pageView.referrer) ?? 0) + 1,
      );
    }
  }

  for (const event of events) {
    visitorIds.add(event.userId ?? event.sessionId);
    if (event.device.browser) {
      browserCounts.set(
        event.device.browser,
        (browserCounts.get(event.device.browser) ?? 0) + 1,
      );
    }

    deviceBreakdown[event.device.device] += 1;
  }

  return {
    totalPageViews: pageViews.length,
    uniqueVisitors: visitorIds.size,
    topPages: [...topPageSummaries.entries()]
      .sort((left, right) => right[1].views - left[1].views)
      .slice(0, topPageLimit ?? 10)
      .map(([url, summary]) => ({
        url,
        views: summary.views,
        uniqueVisitors: summary.visitorIds.size,
        averageTimeMs:
          summary.durationCount > 0
            ? Math.round(summary.durationTotal / summary.durationCount)
            : 0,
      })),
    topReferrers: [...referrerCounts.entries()]
      .sort((left, right) => right[1] - left[1])
      .map(([referrer, visits]) => ({ referrer, visits })),
    deviceBreakdown,
    browserBreakdown: Object.fromEntries(browserCounts.entries()),
  };
}

function buildRealtimeStatsSnapshot(options: {
  events: AnalyticsEvent[];
  pageViews: PageView[];
  now: number;
  windowMs: number;
  topPageLimit?: number;
}): RealtimeStats {
  const { events, pageViews, now, windowMs } = options;
  const visitorIds = new Set<string>();
  const sessionIds = new Set<string>();
  const pageVisitors = new Map<string, Set<string>>();
  const deviceBreakdown: AnalyticsDashboardData["deviceBreakdown"] = {
    desktop: 0,
    tablet: 0,
    mobile: 0,
  };
  const addIdentity = (record: { sessionId: string; userId?: string }) => {
    const visitorId = record.userId ?? record.sessionId;
    visitorIds.add(visitorId);
    sessionIds.add(record.sessionId);
    return visitorId;
  };

  for (const pageView of pageViews) {
    const visitorId = addIdentity(pageView);
    const visitors = pageVisitors.get(pageView.url) ?? new Set<string>();
    visitors.add(visitorId);
    pageVisitors.set(pageView.url, visitors);
  }

  for (const event of events) {
    const visitorId = addIdentity(event);
    const visitors = pageVisitors.get(event.url) ?? new Set<string>();
    visitors.add(visitorId);
    pageVisitors.set(event.url, visitors);
    deviceBreakdown[event.device.device] += 1;
  }

  const conversions = events.filter((event) =>
    event.name.toLowerCase().includes("conversion"),
  ).length;
  const windowSeconds = Math.max(1, windowMs) / 1000;

  return {
    activeVisitors: visitorIds.size,
    currentPageViews: pageViews.length,
    activeSessions: sessionIds.size,
    eventsPerSecond: Number((events.length / windowSeconds).toFixed(2)),
    conversions,
    conversionRate: events.length > 0 ? (conversions / events.length) * 100 : 0,
    deviceBreakdown,
    topActivePages: [...pageVisitors.entries()]
      .sort((left, right) => right[1].size - left[1].size)
      .slice(0, options.topPageLimit ?? 5)
      .map(([url, visitors]) => ({ url, visitors: visitors.size })),
    lastUpdated: now,
  };
}

function matchesFunnelStep(event: AnalyticsEvent, step: FunnelStep): boolean {
  if (event.name !== step.event) return false;
  if (!step.properties) return true;
  return Object.entries(step.properties).every(
    ([key, value]) => event.properties?.[key] === value,
  );
}

function average(values: number[]): number | undefined {
  if (values.length === 0) return undefined;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function analyzeFunnelResults(
  events: AnalyticsEvent[],
  steps: FunnelStep[],
  name: string,
): FunnelResult {
  if (steps.length === 0) {
    return {
      name,
      steps: [],
      totalEntries: 0,
      totalConversions: 0,
      overallConversionRate: 0,
      averageTimeToConvertMs: 0,
      analyzedAt: Date.now(),
    };
  }

  const groupedEvents = new Map<string, AnalyticsEvent[]>();
  for (const event of events) {
    const identity = event.userId ?? event.sessionId;
    const identityEvents = groupedEvents.get(identity) ?? [];
    identityEvents.push(event);
    groupedEvents.set(identity, identityEvents);
  }

  const stepCounts = steps.map(() => 0);
  const stepTimings = steps.map((): number[] => []);
  const conversionTimings: number[] = [];

  for (const identityEvents of groupedEvents.values()) {
    const sortedEvents = [...identityEvents].sort(
      (left, right) => left.timestamp - right.timestamp,
    );
    const matchedTimestamps: number[] = [];
    let searchIndex = 0;

    for (const [stepIndex, step] of steps.entries()) {
      const matchIndex = sortedEvents.findIndex(
        (event, eventIndex) =>
          eventIndex >= searchIndex && matchesFunnelStep(event, step),
      );
      if (matchIndex === -1) break;

      const matchedEvent = sortedEvents[matchIndex];
      if (!matchedEvent) break;

      stepCounts[stepIndex] = (stepCounts[stepIndex] ?? 0) + 1;
      matchedTimestamps.push(matchedEvent.timestamp);

      if (stepIndex > 0) {
        const previousTimestamp = matchedTimestamps[stepIndex - 1];
        if (typeof previousTimestamp === "number") {
          stepTimings[stepIndex]?.push(
            matchedEvent.timestamp - previousTimestamp,
          );
        }
      }

      searchIndex = matchIndex + 1;
    }

    if (matchedTimestamps.length === steps.length) {
      const firstTimestamp = matchedTimestamps[0];
      const lastTimestamp = matchedTimestamps[matchedTimestamps.length - 1];
      if (
        typeof firstTimestamp === "number" &&
        typeof lastTimestamp === "number"
      ) {
        conversionTimings.push(lastTimestamp - firstTimestamp);
      }
    }
  }

  const totalEntries = stepCounts[0] ?? 0;
  const stepResults = steps.map((step, index) => {
    const count = stepCounts[index] ?? 0;
    const previousCount = index === 0 ? count : (stepCounts[index - 1] ?? 0);
    return {
      name: step.name,
      event: step.event,
      count,
      dropoffRate:
        index === 0
          ? 0
          : previousCount > 0
            ? ((previousCount - count) / previousCount) * 100
            : 100,
      conversionRate:
        index === 0 ? 100 : totalEntries > 0 ? (count / totalEntries) * 100 : 0,
      averageTimeFromPreviousMs: average(stepTimings[index] ?? []),
    };
  });
  const totalConversions = stepResults[stepResults.length - 1]?.count ?? 0;

  return {
    name,
    steps: stepResults,
    totalEntries,
    totalConversions,
    overallConversionRate:
      totalEntries > 0 ? (totalConversions / totalEntries) * 100 : 0,
    averageTimeToConvertMs: average(conversionTimings) ?? 0,
    analyzedAt: Date.now(),
  };
}

function dayKey(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function analyzeCohortRetention(
  events: AnalyticsEvent[],
  options: {
    cohortType?: "user" | "session";
    intervals?: number[];
    name: string;
  },
): CohortAnalysis {
  const intervals = [
    ...new Set((options.intervals ?? [1, 7, 30]).filter((day) => day >= 0)),
  ].sort((left, right) => left - right);
  const cohortType = options.cohortType ?? "user";
  const identities = new Map<string, AnalyticsEvent[]>();

  for (const event of events) {
    const identity =
      cohortType === "user"
        ? (event.userId ?? event.sessionId)
        : event.sessionId;
    const identityEvents = identities.get(identity) ?? [];
    identityEvents.push(event);
    identities.set(identity, identityEvents);
  }

  const cohortMap = new Map<string, Map<string, AnalyticsEvent[]>>();
  for (const [identity, identityEvents] of identities) {
    const sorted = [...identityEvents].sort(
      (left, right) => left.timestamp - right.timestamp,
    );
    const first = sorted[0];
    if (!first) continue;

    const bucket = cohortMap.get(dayKey(first.timestamp)) ?? new Map();
    bucket.set(identity, sorted);
    cohortMap.set(dayKey(first.timestamp), bucket);
  }

  return {
    name: options.name,
    cohortType,
    intervals,
    cohorts: [...cohortMap.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([cohort, members]) => {
        const memberLists = [...members.values()];
        const firstSeen =
          memberLists
            .map((list) => list[0]?.timestamp ?? 0)
            .filter(Boolean)
            .sort((left, right) => left - right)[0] ?? 0;

        return {
          cohort,
          size: memberLists.length,
          retained: intervals.map((day) => {
            const retainedUsers = memberLists.filter((list) => {
              const first = list[0]?.timestamp ?? 0;
              return list.some((event) => {
                const diffDays = Math.floor(
                  (event.timestamp - first) / (24 * 60 * 60 * 1000),
                );
                return diffDays === day;
              });
            }).length;

            return {
              day,
              retainedUsers,
              retentionRate:
                memberLists.length > 0
                  ? (retainedUsers / memberLists.length) * 100
                  : 0,
            };
          }),
          lastActivity:
            memberLists
              .flat()
              .map((event) => event.timestamp)
              .sort((left, right) => right - left)[0] ?? firstSeen,
        };
      }),
    generatedAt: Date.now(),
  };
}

function applyCohortSegment(
  events: AnalyticsEvent[],
  segment: Record<string, unknown> | undefined,
): AnalyticsEvent[] {
  if (!segment) return events;
  return events.filter((event) =>
    Object.entries(segment).every(([key, value]) => {
      if (key === "userId") return event.userId === value;
      if (key === "sessionId") return event.sessionId === value;
      return event.properties?.[key] === value;
    }),
  );
}

function createNeonSqlAnalyticsStore(
  execute: NeonSqlExecutor,
  options: NeonAnalyticsStoreOptions = {},
  lifecycle: { shutdown?: () => Promise<void> } = {},
): NeonAnalyticsStore {
  const tenantId = options.tenantId ?? "default";
  const readEvents = async (
    query: AnalyticsEventQuery = {},
  ): Promise<AnalyticsReadResult<AnalyticsEvent>> => {
    const predicates = ["tenant_id = $1"];
    const parameters: unknown[] = [tenantId];
    if (query.userId) {
      appendSqlPredicate(predicates, parameters, "user_id = ?", query.userId);
    }
    if (query.sessionId) {
      appendSqlPredicate(
        predicates,
        parameters,
        "session_id = ?",
        query.sessionId,
      );
    }
    if (query.eventName) {
      appendSqlPredicate(predicates, parameters, "name = ?", query.eventName);
    }
    if (query.eventType) {
      appendSqlPredicate(predicates, parameters, "type = ?", query.eventType);
    }
    if (query.timeRange) {
      appendSqlPredicate(
        predicates,
        parameters,
        "timestamp >= ?",
        query.timeRange.start,
      );
      appendSqlPredicate(
        predicates,
        parameters,
        "timestamp <= ?",
        query.timeRange.end,
      );
    }
    const sql = appendSqlPagination(
      `SELECT id, type, name, properties, url, referrer, session_id, user_id, device, timestamp, COUNT(*) OVER() AS __total_count FROM geenius_analytics_events WHERE ${predicates.join(" AND ")} ORDER BY timestamp ASC`,
      parameters,
      query.limit,
      query.cursor,
    );
    const rows = getRows(await execute(sql, parameters));
    const items = rows.map((row) => ({
      id: row.id as string,
      type: row.type as AnalyticsEvent["type"],
      name: row.name as string,
      properties: row.properties as Record<string, unknown> | undefined,
      url: (row.url as string) ?? "",
      referrer: (row.referrer as string) ?? "",
      sessionId: (row.session_id as string) ?? "",
      userId: row.user_id as string | undefined,
      device: row.device as AnalyticsEvent["device"],
      timestamp: Number(row.timestamp),
    }));
    return sqlReadResult(rows, items, query.limit, query.cursor);
  };
  const readPageViews = async (
    query: AnalyticsPageViewQuery = {},
  ): Promise<AnalyticsReadResult<PageView>> => {
    const predicates = ["tenant_id = $1"];
    const parameters: unknown[] = [tenantId];
    if (query.url) {
      appendSqlPredicate(predicates, parameters, "url = ?", query.url);
    }
    if (query.userId) {
      appendSqlPredicate(predicates, parameters, "user_id = ?", query.userId);
    }
    if (query.sessionId) {
      appendSqlPredicate(
        predicates,
        parameters,
        "session_id = ?",
        query.sessionId,
      );
    }
    if (query.timeRange) {
      appendSqlPredicate(
        predicates,
        parameters,
        "timestamp >= ?",
        query.timeRange.start,
      );
      appendSqlPredicate(
        predicates,
        parameters,
        "timestamp <= ?",
        query.timeRange.end,
      );
    }
    const sql = appendSqlPagination(
      `SELECT url, title, referrer, duration, session_id, user_id, timestamp, COUNT(*) OVER() AS __total_count FROM geenius_analytics_pageviews WHERE ${predicates.join(" AND ")} ORDER BY timestamp ASC`,
      parameters,
      query.limit,
      query.cursor,
    );
    const rows = getRows(await execute(sql, parameters));
    const items = rows.map((row) => ({
      url: (row.url as string) ?? "",
      title: (row.title as string) ?? "",
      referrer: (row.referrer as string) ?? "",
      duration: optionalNumber(row.duration),
      sessionId: (row.session_id as string) ?? "",
      userId: row.user_id as string | undefined,
      timestamp: Number(row.timestamp),
    }));
    return sqlReadResult(rows, items, query.limit, query.cursor);
  };

  return {
    async trackEvent(event) {
      await execute(
        [
          "INSERT INTO geenius_analytics_events",
          "(id, type, name, properties, url, referrer, session_id, user_id, device, timestamp, tenant_id)",
          "VALUES ($1,$2,$3,$4,$5,$6,$7,$8,$9,$10,$11)",
          "ON CONFLICT (id) DO UPDATE SET",
          "type = EXCLUDED.type,",
          "name = EXCLUDED.name,",
          "properties = EXCLUDED.properties,",
          "url = EXCLUDED.url,",
          "referrer = EXCLUDED.referrer,",
          "session_id = EXCLUDED.session_id,",
          "user_id = EXCLUDED.user_id,",
          "device = EXCLUDED.device,",
          "timestamp = EXCLUDED.timestamp,",
          "tenant_id = EXCLUDED.tenant_id",
        ].join(" "),
        [
          event.id,
          event.type,
          event.name,
          event.properties ?? null,
          event.url,
          event.referrer,
          event.sessionId,
          event.userId ?? null,
          event.device,
          event.timestamp,
          tenantId,
        ],
      );
      return event;
    },
    async trackPageView(pageView) {
      await execute(
        "INSERT INTO geenius_analytics_pageviews (url, title, referrer, duration, session_id, user_id, timestamp, tenant_id) VALUES ($1,$2,$3,$4,$5,$6,$7,$8)",
        [
          pageView.url,
          pageView.title,
          pageView.referrer,
          pageView.duration ?? null,
          pageView.sessionId,
          pageView.userId ?? null,
          pageView.timestamp,
          tenantId,
        ],
      );
      return pageView;
    },
    async identifyUser(userId, traits) {
      await execute(
        "INSERT INTO geenius_analytics_users (user_id, traits, updated_at, tenant_id) VALUES ($1,$2,$3,$4) ON CONFLICT (user_id) DO UPDATE SET traits = EXCLUDED.traits, updated_at = EXCLUDED.updated_at, tenant_id = EXCLUDED.tenant_id",
        [userId, traits ?? null, Date.now(), tenantId],
      );
    },
    async setUserProperties(userId, traits) {
      await execute(
        [
          "INSERT INTO geenius_analytics_users",
          "(user_id, traits, updated_at, tenant_id)",
          "VALUES ($1,$2,$3,$4)",
          "ON CONFLICT (user_id) DO UPDATE SET",
          "traits = COALESCE(geenius_analytics_users.traits, '{}'::jsonb) || EXCLUDED.traits,",
          "updated_at = EXCLUDED.updated_at,",
          "tenant_id = EXCLUDED.tenant_id",
        ].join(" "),
        [userId, traits, Date.now(), tenantId],
      );
    },
    async listEvents() {
      return (await readEvents()).items;
    },
    async getEvents(query = {}) {
      return readEvents(query);
    },
    async getPageViews(query = {}) {
      return readPageViews(query);
    },
    async getDashboardData(query = {}) {
      const pageViews = (
        await this.getPageViews({ timeRange: query.timeRange })
      ).items;
      const events = (await this.getEvents({ timeRange: query.timeRange }))
        .items;
      return buildDashboardData(pageViews, events, query.limit);
    },
    async getRealtimeStats(query = {}) {
      const now = query.now ?? Date.now();
      const since =
        query.since ?? now - Math.max(1, query.windowMs ?? 5 * 60 * 1000);
      const timeRange = { start: since, end: now };
      const events = (await this.getEvents({ timeRange })).items;
      const pageViews = (await this.getPageViews({ timeRange })).items;
      return buildRealtimeStatsSnapshot({
        events,
        pageViews,
        now,
        windowMs: now - since,
        topPageLimit: query.topPageLimit,
      });
    },
    async getFunnelResults(query) {
      const events = (
        await this.getEvents({
          timeRange: query.timeRange,
          limit: query.limit,
        })
      ).items;
      return analyzeFunnelResults(events, query.steps, "Neon Funnel");
    },
    async getCohortAnalysis(query = {}) {
      const events = applyCohortSegment(
        (await this.getEvents({ timeRange: query.timeRange })).items,
        query.segment,
      );
      return analyzeCohortRetention(events, {
        cohortType: query.cohortType,
        intervals: query.intervals,
        name: "Neon Cohort Analysis",
      });
    },
    async flush() {
      return undefined;
    },
    async shutdown() {
      await lifecycle.shutdown?.();
    },
    async purgeExpiredData(cutoffTimestamp) {
      await execute(
        "DELETE FROM geenius_analytics_events WHERE timestamp < $1 AND tenant_id = $2",
        [cutoffTimestamp, tenantId],
      );
      await execute(
        "DELETE FROM geenius_analytics_pageviews WHERE timestamp < $1 AND tenant_id = $2",
        [cutoffTimestamp, tenantId],
      );
      await execute(
        "DELETE FROM geenius_analytics_users WHERE updated_at < $1 AND tenant_id = $2",
        [cutoffTimestamp, tenantId],
      );
    },
    async deleteUserData(userId) {
      await execute(
        "DELETE FROM geenius_analytics_events WHERE user_id = $1 AND tenant_id = $2",
        [userId, tenantId],
      );
      await execute(
        "DELETE FROM geenius_analytics_pageviews WHERE user_id = $1 AND tenant_id = $2",
        [userId, tenantId],
      );
      await execute(
        "DELETE FROM geenius_analytics_users WHERE user_id = $1 AND tenant_id = $2",
        [userId, tenantId],
      );
    },
    async runMigrations() {
      await runAnalyticsMigrations(execute);
    },
  };
}

function createNeonMemoryAnalyticsStore(): NeonAnalyticsStore {
  const events = new Map<string, AnalyticsEvent>();
  const pageViews: PageView[] = [];
  const users = new Map<string, { traits?: UserTraits; updatedAt: number }>();

  return {
    async trackEvent(event) {
      events.set(event.id, event);
      return event;
    },
    async trackPageView(pageView) {
      pageViews.push(pageView);
      return pageView;
    },
    async identifyUser(userId, traits) {
      users.set(userId, {
        traits,
        updatedAt: Date.now(),
      });
    },
    async setUserProperties(userId, traits) {
      const current = users.get(userId);
      users.set(userId, {
        traits: {
          ...current?.traits,
          ...traits,
        },
        updatedAt: Date.now(),
      });
    },
    async listEvents() {
      return [...events.values()].sort(
        (left, right) => left.timestamp - right.timestamp,
      );
    },
    async getEvents(query = {}) {
      const filtered = filterEvents(await this.listEvents(), query);
      return paginate(filtered, query.limit, query.cursor);
    },
    async getPageViews(query = {}) {
      const filtered = filterPageViews(
        [...pageViews].sort((left, right) => left.timestamp - right.timestamp),
        query,
      );
      return paginate(filtered, query.limit, query.cursor);
    },
    async getDashboardData(query = {}) {
      const pageViewResult = await this.getPageViews({
        timeRange: query.timeRange,
      });
      const eventResult = await this.getEvents({ timeRange: query.timeRange });
      return buildDashboardData(
        pageViewResult.items,
        eventResult.items,
        query.limit,
      );
    },
    async getRealtimeStats(query = {}) {
      const now = query.now ?? Date.now();
      const since =
        query.since ?? now - Math.max(1, query.windowMs ?? 5 * 60 * 1000);
      const timeRange = { start: since, end: now };
      const events = (await this.getEvents({ timeRange })).items;
      const activePageViews = (await this.getPageViews({ timeRange })).items;
      return buildRealtimeStatsSnapshot({
        events,
        pageViews: activePageViews,
        now,
        windowMs: now - since,
        topPageLimit: query.topPageLimit,
      });
    },
    async getFunnelResults(query) {
      const events = (
        await this.getEvents({
          timeRange: query.timeRange,
          limit: query.limit,
        })
      ).items;
      return analyzeFunnelResults(events, query.steps, "Neon Funnel");
    },
    async getCohortAnalysis(query = {}) {
      const events = applyCohortSegment(
        (await this.getEvents({ timeRange: query.timeRange })).items,
        query.segment,
      );
      return analyzeCohortRetention(events, {
        cohortType: query.cohortType,
        intervals: query.intervals,
        name: "Neon Cohort Analysis",
      });
    },
    async flush() {
      return undefined;
    },
    async shutdown() {
      events.clear();
      pageViews.splice(0, pageViews.length);
      users.clear();
    },
    async purgeExpiredData(cutoffTimestamp) {
      for (const [id, event] of events) {
        if (event.timestamp < cutoffTimestamp) {
          events.delete(id);
        }
      }
      for (let index = pageViews.length - 1; index >= 0; index -= 1) {
        const pageView = pageViews[index];
        if (pageView && pageView.timestamp < cutoffTimestamp) {
          pageViews.splice(index, 1);
        }
      }
      for (const [userId, user] of users) {
        if (user.updatedAt < cutoffTimestamp) {
          users.delete(userId);
        }
      }
    },
    async deleteUserData(userId) {
      for (const [id, event] of events) {
        if (event.userId === userId) {
          events.delete(id);
        }
      }

      for (let index = pageViews.length - 1; index >= 0; index -= 1) {
        const pageView = pageViews[index];
        if (pageView?.userId === userId) {
          pageViews.splice(index, 1);
        }
      }

      users.delete(userId);
    },
    async runMigrations() {
      return undefined;
    },
  };
}

function createNeonAnalyticsStoreFromExecutor(
  execute: NeonSqlExecutor,
  options: NeonAnalyticsStoreOptions = {},
  lifecycle: { shutdown?: () => Promise<void> } = {},
): NeonAnalyticsStore {
  return createNeonSqlAnalyticsStore(execute, options, lifecycle);
}

/**
 * Creates a Neon analytics store with injectable SQL execution.
 *
 * @param options Store options, including an optional SQL executor for tests or serverless runtimes.
 * @returns A Neon analytics store implementation.
 */
export function createNeonAnalyticsStore(
  options: NeonAnalyticsStoreOptions = {},
): NeonAnalyticsStore {
  return options.execute
    ? createNeonAnalyticsStoreFromExecutor(options.execute, options)
    : createNeonMemoryAnalyticsStore();
}

/**
 * Creates a Neon analytics store from any query-capable driver client.
 *
 * @param client A Neon Pool, Client, or compatible query client.
 * @returns A Neon analytics store backed by the supplied driver client.
 */
export function createNeonAnalyticsStoreFromClient(
  client: NeonAnalyticsClient,
  options: Omit<NeonAnalyticsStoreOptions, "execute"> = {},
): NeonAnalyticsStore {
  return createNeonAnalyticsStoreFromExecutor((sql, parameters) =>
    client.query(sql, parameters),
    options,
  );
}

/**
 * Creates a Neon analytics store from a Neon connection string.
 *
 * @param connectionString Neon/Postgres connection string.
 * @returns A Neon analytics store backed by a native `@neondatabase/serverless` Pool.
 */
export async function createNeonAnalyticsStoreFromConnectionString(
  connectionString: string,
): Promise<NeonAnalyticsStore> {
  const { Pool } = await import("@neondatabase/serverless");
  const pool = new Pool({ connectionString });
  return createNeonAnalyticsStoreFromExecutor(
    (sql, parameters) => pool.query(sql, parameters),
    {},
    { shutdown: () => pool.end() },
  );
}
